import { useShowToast } from '@/hooks';
import {
  ACCEPTED_FILE_TYPES,
  FILE_SIGNATURES,
  FILE_EXTENSION_MAP,
} from '@/constants/files';

export const useFileValidation = () => {
  const toast = useShowToast();

  const showToast = ({ title, toastConfig = {} }) => {
    toast({ title, status: 'error', ...toastConfig });
  };

  const showCorruptedFileToast = (toastConfig = {}) =>
    showToast({
      title:
        'Upload failed because the file is damaged. Try again with a different file.',
      ...toastConfig,
    });

  const showUnsupportedFileTypeToast = (toastConfig = {}) =>
    showToast({
      title: 'Unsupported file type. Try again with a different file.',
      ...toastConfig,
    });

  const showEmptyFileToast = (toastConfig = {}) =>
    showToast({
      title:
        'Upload failed because the file is empty. Try again with a different file.',
      ...toastConfig,
    });

  // Flatten FILE_EXTENSION_MAP for lookup
  const flatMimeToExtMap = Object.values(FILE_EXTENSION_MAP).reduce(
    (acc, group) => {
      return { ...acc, ...group };
    },
    {}
  );

  const getFileExtension = (file) => {
    const name = file?.name || file?.filename || '';
    return name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  };

  const getFileExtensionForType = (fileType) =>
    flatMimeToExtMap[fileType] || null;

  const getIsAcceptedFileType = (file) => {
    if (!file) return false;
    const fileType = file?.type || file?.content_type;
    const extension = getFileExtension(file);

    return (
      Object.values(ACCEPTED_FILE_TYPES).some((category) =>
        Object.values(category).includes(fileType)
      ) ||
      Object.values(ACCEPTED_FILE_TYPES.otherExtensions).includes(
        `.${extension}`
      ) ||
      `.${extension}` === ACCEPTED_FILE_TYPES.archives.GZ_EXT
    );
  };

  const validateFileSignature = (file, toastConfig = {}) =>
    new Promise((resolve) => {
      if (!file || (!file.slice && !(file instanceof Blob)))
        return resolve(true);

      const reader = new FileReader();
      reader.onload = (event) => {
        const content = new Uint8Array(event.target.result);
        const fileType = file?.type || file?.content_type;
        const extension = getFileExtension(file);
        const targetExt = extension || getFileExtensionForType(fileType);

        const extUpper = targetExt?.toUpperCase();
        const signature =
          FILE_SIGNATURES?.documents?.[extUpper] ||
          FILE_SIGNATURES?.images?.[extUpper] ||
          FILE_SIGNATURES?.archives?.[extUpper] ||
          FILE_SIGNATURES?.archives?.[`${extUpper}_EXT`] ||
          FILE_SIGNATURES?.spreadsheets?.[extUpper] ||
          FILE_SIGNATURES?.presentations?.[extUpper] ||
          FILE_SIGNATURES?.otherExtensions?.[`${extUpper}_EXT`] ||
          null;

        if (signature) {
          // A signature may list alternatives (TIFF's two byte orders).
          const candidates = Array.isArray(signature[0])
            ? signature
            : [signature];
          const isValid = candidates.some((candidate) =>
            candidate.every((byte, idx) => content[idx] === byte)
          );
          if (!isValid) showCorruptedFileToast(toastConfig);
          return resolve(isValid);
        }

        return resolve(true);
      };

      reader.onerror = () => {
        showCorruptedFileToast(toastConfig);
        resolve(false);
      };

      try {
        const blob = file?.slice(0, 20);
        reader.readAsArrayBuffer(blob);
      } catch (err) {
        console.error('Error reading file:', err);
        resolve(false);
      }
    });

  const isSupportedFileType = (file, toastConfig = {}) => {
    if (!file) return false;
    const size = file?.size || file?.byte_size || 0;
    if (size === 0) {
      showEmptyFileToast(toastConfig);
      return false;
    }

    if (!getIsAcceptedFileType(file)) {
      showUnsupportedFileTypeToast(toastConfig);
      return false;
    }

    return true;
  };

  const validateImageFile = async (file, toastConfig = {}) => {
    if (!file) return false;
    if (!file?.slice && !(file instanceof Blob))
      return getIsAcceptedFileType(file);

    const isValidSig = await validateFileSignature(file, toastConfig);
    if (!isValidSig) return false;

    const ext = getFileExtension(file);
    const type = file?.type || file?.content_type;

    const validImageTypes = {
      jpg: ACCEPTED_FILE_TYPES.images.JPEG,
      jpeg: ACCEPTED_FILE_TYPES.images.JPEG,
      png: ACCEPTED_FILE_TYPES.images.PNG,
      gif: ACCEPTED_FILE_TYPES.images.GIF,
      bmp: ACCEPTED_FILE_TYPES.images.BMP,
      tiff: ACCEPTED_FILE_TYPES.images.TIFF,
    };

    if (
      ext in validImageTypes &&
      type !== validImageTypes[ext] &&
      !(
        (ext === 'jpg' || ext === 'jpeg') &&
        (type === ACCEPTED_FILE_TYPES.images.JPG ||
          type === ACCEPTED_FILE_TYPES.images.JPEG)
      )
    ) {
      showCorruptedFileToast(toastConfig);
      return false;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(true);
      reader.onerror = () => {
        showCorruptedFileToast();
        resolve(false);
      };
      try {
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error reading image:', error);
        resolve(false);
      }
    });
  };

  const validatePdfFile = async (file, toastConfig = {}) => {
    if (!file) return false;
    if (!file?.slice && !(file instanceof Blob))
      return getIsAcceptedFileType(file);

    const isValidSig = await validateFileSignature(file, toastConfig);
    if (!isValidSig) return false;

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = new Uint8Array(event.target.result);
        const header = new TextDecoder().decode(content.slice(0, 5));
        if (header !== '%PDF-') {
          showCorruptedFileToast(toastConfig);
          return resolve(false);
        }
        resolve(true);
      };
      reader.onerror = () => {
        showCorruptedFileToast(toastConfig);
        resolve(false);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const validateDocumentFile = async (file, toastConfig = {}) => {
    if (!file) return false;
    if (!file?.slice && !(file instanceof Blob))
      return getIsAcceptedFileType(file);

    const extension = getFileExtension(file);
    const fileType = file?.type || file?.content_type;

    const officeExtensions = ['docx', 'xlsx', 'pptx'];
    if (
      officeExtensions.includes(extension) ||
      fileType === ACCEPTED_FILE_TYPES.documents.DOCX
    ) {
      const isValidSig = await validateFileSignature(file, toastConfig);
      if (!isValidSig) return false;
    }

    const docTypes = {
      doc: [ACCEPTED_FILE_TYPES.documents.DOC],
      docx: [ACCEPTED_FILE_TYPES.documents.DOCX],
      pdf: [ACCEPTED_FILE_TYPES.documents.PDF],
      txt: [ACCEPTED_FILE_TYPES.documents.TXT],
      rtf: [
        ACCEPTED_FILE_TYPES.documents.RTF,
        ACCEPTED_FILE_TYPES.documents.RTF_TEXT,
      ],
    };

    if (extension in docTypes && !docTypes[extension].includes(fileType)) {
      showUnsupportedFileTypeToast(toastConfig);
      return false;
    }

    return true;
  };

  const handleAttachmentValidation = async ({
    files: attachments,
    toastConfig = {},
  }) => {
    if (attachments instanceof FileList) attachments = Array.from(attachments);
    if (!attachments || !Array.isArray(attachments)) return true;

    try {
      const validAttachments = attachments.filter(Boolean);
      if (validAttachments.length === 0) return true;

      if (!validAttachments[0]?.slice && validAttachments[0]?.content_type) {
        return validAttachments.every(
          (att) => (att?.byte_size || 0) > 0 && getIsAcceptedFileType(att)
        );
      }

      const results = await Promise.all(
        validAttachments.map(async (file) => {
          if (!isSupportedFileType(file, toastConfig)) return false;

          const extension = getFileExtension(file);
          const fileType = file?.type || file?.content_type;

          if (
            fileType?.startsWith('image/') ||
            ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'].includes(extension)
          ) {
            return await validateImageFile(file, toastConfig);
          }

          if (
            fileType === ACCEPTED_FILE_TYPES.documents.PDF ||
            extension === 'pdf'
          ) {
            return await validatePdfFile(file, toastConfig);
          }

          if (
            Object.values(ACCEPTED_FILE_TYPES.documents).includes(fileType) ||
            ['doc', 'docx', 'rtf', 'txt'].includes(extension)
          ) {
            return await validateDocumentFile(file, toastConfig);
          }

          return true; // Fallback for other accepted types
        })
      );

      return results.every(Boolean);
    } catch (error) {
      console.error('Attachment validation error:', error);
      return false;
    }
  };

  return {
    handleAttachmentValidation,
    getIsAcceptedFileType,
  };
};
